import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Ban, Shield } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CyFunLevelBadge } from "@/components/risk/CyFunLevelBadge";
import { SectorReviewCard } from "@/components/risk-wizard/SectorReviewCard";
import { useGetDefaultMatrix } from "@/hooks/api/query/useGetDefaultMatrix";
import {
  collectMatrixModifications,
  filterValidJustifications,
} from "@/utils/riskMatrixModifications";
import { formatScore } from "@/lib/utils";
import type {
  CyFunLevel,
  JustificationValue,
  OrganizationSize,
  RiskAnalysisItem,
  Sector,
  SectorAnalysis,
  SectorJustifications,
} from "@/types/risk-analysis";

type ResultStepProps = {
  sessionId: string;
  companyName: string;
  organizationSize: OrganizationSize | null;
  globalScore: number;
  globalCyFunLevel: CyFunLevel;
  totalModifications: number;
  riskAnalyses: RiskAnalysisItem[];
  sectorAnalysesMap: Record<string, SectorAnalysis>;
  skippedSectorIds: Set<string>;
  sectors: Sector[];
  initialJustifications: Record<string, SectorJustifications>;
  onJustificationsChange: (
    justifications: Record<string, SectorJustifications>
  ) => void;
  onEditSector: (sectorId: string) => void;
  onAllModificationsJustified?: (allJustified: boolean) => void;
  showValidation?: boolean;
};

function SectorReviewCardWithDiff({
  sessionId,
  riskAnalysis,
  analysis,
  isSkipped,
  isIncomplete,
  sectorName,
  sectorCode,
  justifications,
  onJustificationChange,
  onEdit,
  onAllJustified,
  showValidation,
}: {
  sessionId: string;
  riskAnalysis: RiskAnalysisItem;
  analysis: SectorAnalysis | undefined;
  isSkipped: boolean;
  isIncomplete: boolean;
  sectorName: string;
  sectorCode: string;
  justifications: Record<string, string>;
  onJustificationChange: (modId: string, value: string) => void;
  onEdit: () => void;
  onAllJustified?: (allJustified: boolean) => void;
  showValidation?: boolean;
}) {
  const needsDefaultFetch = !isSkipped && !isIncomplete && !!analysis;

  const { data: defaultMatrixData } = useGetDefaultMatrix(
    sessionId,
    riskAnalysis.sector_id,
    needsDefaultFetch
  );

  const modifications = useMemo(() => {
    if (!needsDefaultFetch || !defaultMatrixData) return [];

    return collectMatrixModifications(
      riskAnalysis.matrix,
      defaultMatrixData,
      sectorCode,
      sectorName
    );
  }, [
    needsDefaultFetch,
    defaultMatrixData,
    riskAnalysis.matrix,
    sectorCode,
    sectorName,
  ]);

  const filteredJustifications = useMemo(
    () => filterValidJustifications(justifications, modifications),
    [justifications, modifications]
  );

  return (
    <SectorReviewCard
      sectorName={sectorName}
      sectorCode={sectorCode}
      score={isSkipped ? null : (analysis?.totalScore ?? null)}
      cyfunLevel={isSkipped ? null : (analysis?.cyfunLevel ?? null)}
      modifications={modifications}
      isSkipped={isSkipped}
      isIncomplete={isIncomplete}
      justifications={filteredJustifications}
      onJustificationChange={onJustificationChange}
      onEdit={onEdit}
      onAllJustified={onAllJustified}
      showValidation={showValidation}
    />
  );
}

export function ResultStep({
  sessionId,
  companyName,
  organizationSize,
  globalScore,
  globalCyFunLevel,
  totalModifications,
  riskAnalyses,
  sectorAnalysesMap,
  skippedSectorIds,
  sectors,
  initialJustifications,
  onJustificationsChange,
  onEditSector,
  onAllModificationsJustified,
  showValidation,
}: ResultStepProps) {
  const sectorLookup = useMemo(() => {
    const map: Record<string, Sector> = {};
    for (const s of sectors) {
      map[s.id] = s;
    }
    return map;
  }, [sectors]);

  const [justificationsMap, setJustificationsMap] = useState<
    Record<string, SectorJustifications>
  >(initialJustifications);

  useEffect(() => {
    setJustificationsMap(initialJustifications);
  }, [initialJustifications]);

  const handleJustificationChange = useCallback(
    (sectorId: string, modId: string, value: JustificationValue) => {
      setJustificationsMap((prev) => {
        const sectorJustifications: SectorJustifications = {
          ...(prev[sectorId] ?? {}),
          [modId]: value,
        };
        const updated: Record<string, SectorJustifications> = {
          ...prev,
          [sectorId]: sectorJustifications,
        };
        onJustificationsChange(updated);
        return updated;
      });
    },
    [onJustificationsChange]
  );

  const [sectorJustifiedMap, setSectorJustifiedMap] = useState<
    Record<string, boolean>
  >({});

  const allModificationsJustified = riskAnalyses.every((ra) => {
    if (skippedSectorIds.has(ra.sector_id)) return true;
    if (!ra.has_populated_matrix) return true;
    return sectorJustifiedMap[ra.sector_id] !== false;
  });

  useEffect(() => {
    onAllModificationsJustified?.(allModificationsJustified);
  }, [allModificationsJustified, onAllModificationsJustified]);

  const analyzedCount = riskAnalyses.filter(
    (ra) => !skippedSectorIds.has(ra.sector_id)
  ).length;
  const hasIncompleteSectors = riskAnalyses.some(
    (ra) => !ra.has_populated_matrix && !skippedSectorIds.has(ra.sector_id)
  );
  const hasSkippedSectors = riskAnalyses.some((ra) =>
    skippedSectorIds.has(ra.sector_id)
  );

  return (
    <div className="space-y-4 md:space-y-8">
      {hasIncompleteSectors && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Trebuie sa completati analiza pentru toate sectoarele selectate
            pentru a putea genera raportul.
          </AlertDescription>
        </Alert>
      )}

      {hasSkippedSectors && (
        <Alert className="border-amber-200 bg-amber-50">
          <Ban className="h-4 w-4" />
          <AlertDescription>
            Aveți sectoare omise. Toate sectoarele trebuie completate pentru a
            putea genera raportul PDF.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base md:text-lg">
            <Shield className="h-4 w-4 md:h-5 md:w-5" />
            Aspecte Generale
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 md:space-y-4">
          <p>
            Finalizarea evaluării nivelului de risc al entității:{" "}
            <strong>{companyName}</strong>
          </p>
          <p>
            Evaluarea s-a realizat pentru {analyzedCount} sectoare. Modificări
            totale: {totalModifications}
          </p>

          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 space-y-2 md:p-4">
            <p>
              Scorul maxim asociat serviciilor furnizate:{" "}
              <strong>{formatScore(globalScore)}</strong>
            </p>
            <div className="flex items-center gap-2">
              <span>Categoria de cerințe de securitate cibernetică:</span>
              <CyFunLevelBadge level={globalCyFunLevel} />
            </div>
          </div>

          <Alert className="border-amber-200 bg-amber-50">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              ATENȚIE! Aveți obligația de a implementa categoria de cerințe de
              securitate cibernetică, nivel {globalCyFunLevel}.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      <div className="space-y-3 md:space-y-4">
        <h3 className="font-heading text-base font-semibold md:text-lg">
          Sectoare Analizate
        </h3>
        <div className="grid gap-3 grid-cols-1 md:gap-4">
          {riskAnalyses.map((ra) => {
            const analysis = sectorAnalysesMap[ra.sector_id];
            const isSkipped = skippedSectorIds.has(ra.sector_id);
            const isIncomplete = !ra.has_populated_matrix && !isSkipped;
            const sector = sectorLookup[ra.sector_id];
            return (
              <SectorReviewCardWithDiff
                key={ra.sector_id}
                sessionId={sessionId}
                riskAnalysis={ra}
                analysis={analysis}
                isSkipped={isSkipped}
                isIncomplete={isIncomplete}
                sectorName={
                  analysis?.sectorName ?? sector?.nume ?? ra.sector_id
                }
                sectorCode={ra.sector_id}
                justifications={justificationsMap[ra.sector_id] ?? {}}
                onJustificationChange={(modId, value) =>
                  handleJustificationChange(
                    ra.sector_id,
                    modId,
                    value as JustificationValue
                  )
                }
                onEdit={() => onEditSector(ra.sector_id)}
                onAllJustified={(justified) =>
                  setSectorJustifiedMap((prev) => ({
                    ...prev,
                    [ra.sector_id]: justified,
                  }))
                }
                showValidation={showValidation}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
